import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { vacancyService } from '../../services/vacancyService';
import { authService } from '../../services/authService';
import EmployeeBottomNavigation from '../../components/EmployeeBottomNavigation';
import { toast } from 'react-toastify';
import EmployeeNav from '../../components/EmployeeNav';
import Loader from '../../components/Loader';
import { useTranslation } from '../../hooks/useTranslation';

const SavedVacancies = () => {
    const navigate = useNavigate();
    const user = authService.getCurrentUser();
    const { t, locale } = useTranslation();
    const [activeTab, setActiveTab] = useState('saved'); // 'saved' or 'applied'
    const [savedVacancies, setSavedVacancies] = useState([]);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [savedResponse, applicationsResponse] = await Promise.all([
                vacancyService.getSaved(),
                vacancyService.getApplications()
            ]);

            if (savedResponse.success) {
                setSavedVacancies(savedResponse.data || []);
            }

            if (applicationsResponse.success) {
                setApplications(applicationsResponse.data || []);
            }
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error(t('employee.savedVacancies.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleUnsave = async (vacancyId) => {
        try {
            await vacancyService.unsave(vacancyId);
            setSavedVacancies(prev => prev.filter(v => v.id !== vacancyId));
            toast.success(t('employee.savedVacancies.unsaveSuccess'));
        } catch (error) {
            toast.error(t('employee.savedVacancies.unsaveFailed'));
        }
    };

    const handleApply = (vacancyId) => {
        navigate(`/employee/vacancies/${vacancyId}/apply`);
    };

    const handleJobClick = (jobId) => {
        navigate(`/employee/vacancies/${jobId}`);
    };

    const hasApplied = (vacancyId) => {
        return applications.some(app => app.vacancy?.id === vacancyId);
    };

    const formatSalary = (salary, currency) => {
        if (!salary) return t('employee.home.salaryNotSpecified');
        if (salary >= 1000) {
            return `${Math.round(salary / 1000)}k ${currency} ${t('employee.home.perMonth')}`;
        }
        return `${salary} ${currency} ${t('employee.home.perMonth')}`;
    };

    const formatWorkType = (type) => {
        const types = {
            'full_time': t('employee.home.workType.fullTime'),
            'part_time': t('employee.home.workType.partTime'),
            'contract': t('employee.home.workType.contract'),
            'freelance': t('employee.home.workType.freelance')
        };
        return types[type] || type;
    };

    const formatPresenceType = (type) => {
        const types = {
            'remote': t('employee.home.presenceType.remote'),
            'hybrid': t('employee.home.presenceType.hybrid'),
            'onsite': t('employee.home.presenceType.onsite')
        };
        return types[type] || type;
    };

    const formatLocation = (location) => {
        if (!location) return 'القاهرة';
        if (typeof location === 'string') return location;
        if (typeof location === 'object' && (location.lat || location.long)) {
            return 'القاهرة';
        }
        return 'القاهرة';
    };

    // Get vacancies based on active tab
    const getDisplayedVacancies = () => {
        if (activeTab === 'applied') {
            return applications.map(app => ({
                ...app.vacancy,
                is_applied: true,
                application_status: app.status
            }));
        } else {
            return savedVacancies.map(vacancy => ({
                ...vacancy,
                is_applied: hasApplied(vacancy.id)
            }));
        }
    };

    const displayedVacancies = getDisplayedVacancies();

    return (
        <div className="employee-home-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            {/* Header with Notification and Profile */}
            <EmployeeNav user={user} />

            {/* Filter Tabs */}
            <div className="filter-tabs-container px-3 mb-3">
                <div className="filter-tabs d-flex gap-2">
                    <button
                        className={`filter-tab ${activeTab === 'applied' ? 'active' : ''}`}
                        onClick={() => setActiveTab('applied')}
                    >
                        {t('employee.savedVacancies.appliedJobs')}
                    </button>
                    <button
                        className={`filter-tab ${activeTab === 'saved' ? 'active' : ''}`}
                        onClick={() => setActiveTab('saved')}
                    >
                        {t('employee.savedVacancies.savedJobs')}
                    </button>
                </div>
            </div>

            {/* Vacancies List */}
            <div className="all-jobs-section px-3 mb-5">
                <div className="jobs-list">
                    {loading ? (
                        <Loader size="medium" />
                    ) : displayedVacancies.length > 0 ? (
                        displayedVacancies.map((job) => {
                            const isApplied = job.is_applied || hasApplied(job.id);
                            return (
                                <div key={job.id} className="job-card mb-3">
                                    <div className="d-flex align-items-start gap-3">
                                        <div className="company-logo-card">
                                            {job.company?.logo ? (
                                                <img
                                                    src={job.company?.logo}
                                                    alt="Company Logo"
                                                    className="logo-img"
                                                />
                                            ) : (
                                                <div className="company-logo-text text-dark align-content-center">
                                                    <b>
                                                        {job.company?.name?.charAt(0).toUpperCase() || 'C'}
                                                    </b>
                                                </div>
                                            )}
                                        </div>
                                        <div className="job-content flex-grow-1">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div className="flex-grow-1">
                                                    <div className="job-title-card mb-1">
                                                        {job.job_title?.name || t('employee.home.jobTitle')}
                                                    </div>
                                                    <div className="job-company-location mb-2">
                                                        {job.company?.name || t('employee.home.companyName')} - {formatLocation(job.location)} - مصر
                                                    </div>
                                                    <div className="job-salary mb-2">
                                                        {formatSalary(job.salary, job.currency)}
                                                    </div>
                                                    <div className="job-tags d-flex gap-2 mb-2">
                                                        <span className="job-tag">{formatPresenceType(job.presence_type)}</span>
                                                        <span className="job-tag">{formatWorkType(job.type_of_work)}</span>
                                                        <button
                                                            className={`btn-apply ${isApplied ? 'btn-applied' : ''}`}
                                                            onClick={() => isApplied ? null : handleApply(job.id)}
                                                            disabled={isApplied}
                                                        >
                                                            {isApplied ? t('employee.savedVacancies.applied') : t('employee.savedVacancies.apply')}
                                                        </button>
                                                    </div>
                                                </div>
                                                <button className="job-menu-btn">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <circle cx="12" cy="12" r="1" />
                                                        <circle cx="12" cy="5" r="1" />
                                                        <circle cx="12" cy="19" r="1" />
                                                    </svg>
                                                </button>

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-center py-4">{t('employee.savedVacancies.noJobs')}</div>
                    )}
                </div>
            </div>

            {/* Bottom Navigation Bar */}
            <EmployeeBottomNavigation />
        </div>
    );
};

export default SavedVacancies;
